/**
 * Email Module — Centralized API service
 * All email endpoints go through axiosConfig (shared auth, baseURL).
 */
import axiosConfig from '../../../config/axiosConfig';

const EMAIL_BASE = '/email';

/* ──────────────── helpers ──────────────── */
const get  = (path, params) => axiosConfig.get(`${EMAIL_BASE}${path}`, { params });
const post = (path, body)   => axiosConfig.post(`${EMAIL_BASE}${path}`, body);
const put  = (path, body)   => axiosConfig.put(`${EMAIL_BASE}${path}`, body);
const del  = (path, body)   => axiosConfig.delete(`${EMAIL_BASE}${path}`, body ? { data: body } : undefined);
const patch = (path, body)  => axiosConfig.patch(`${EMAIL_BASE}${path}`, body);

/* ──────────────── Sender Profile APIs ──────────────── */
export const senderProfileApi = {
  get:          ()     => get('/sender-profile'),
  update:       (data) => put('/sender-profile', data),
};

/* ──────────────── Enterprise Domain APIs ──────────────── */
export const enterpriseApi = {
  registerDomain:      (domain, customFromPrefix) => post('/enterprise/domain/register', { domain, customFromPrefix }),
  listDomains:         ()                         => get('/enterprise/domain/list'),
  getDnsRecords:       (domain)                   => get('/enterprise/domain/dns-records', domain ? { domain } : undefined),
  verifyStatus:        (domain)                   => get('/enterprise/domain/verify-status', domain ? { domain } : undefined),
  setActiveDomain:     (domain)                   => put('/enterprise/domain/set-active', { domain }),
  removeDomain:        (domain)                   => del('/enterprise/domain/remove', domain ? { domain } : undefined),
  updateDomainSettings:(data)                     => put('/enterprise/domain/settings', data),
};

/* ──────────────── Config APIs ──────────────── */
export const configApi = {
  getAll:         ()                     => get('/config/'),
  saveSES:        (data)                 => post('/config/ses', data),
  saveSMTP:       (data)                 => post('/config/smtp', data),
  deleteProvider: (provider)             => del(`/config/${provider}`),
  testProvider:   (provider, testEmail)  => post(`/config/${provider}/test`, { testEmail }),
  getUsage:       (provider)             => get(`/config/${provider}/usage`),
  toggleStatus:   (provider, isActive)   => patch(`/config/${provider}/status`, { isActive }),
};

/* ──────────────── Identity / SES Scoping APIs ──────────────── */
export const identityApi = {
  list:              ()           => get('/identity/'),
  listVerified:      ()           => get('/identity/verified'),
  verifyDomain:      (domain)     => post('/identity/verify-domain', { domain }),
  verifyEmail:       (email)      => post('/identity/verify-email', { email }),
  checkStatus:       (identityId) => get(`/identity/${identityId}/status`),
  remove:            (identityId) => del(`/identity/${identityId}`),
  sesAccount:        ()           => get('/identity/ses-account'),
  sendingConfig:     ()           => get('/identity/sending-config'),
  updateSendingConfig: (data)     => put('/identity/sending-config', data),
  validateEmails:    (emails)     => post('/identity/validate-emails', { emails }),
};

/* ──────────────── Onboarding APIs ──────────────── */
export const onboardingApi = {
  getStatus:     ()           => get('/identity/onboarding/status'),
  autoDetect:    ()           => get('/identity/onboarding/auto-detect'),
  completeStep:  (stepName)   => post('/identity/onboarding/complete-step', { stepName }),
  resetStep:     (stepName)   => post('/identity/onboarding/reset-step', { stepName }),
};

/* ──────────────── Template APIs ──────────────── */
export const templateApi = {
  list:               (params)         => get('/templates/', params),
  create:             (data)           => post('/templates/', data),
  getById:            (id)             => get(`/templates/${id}`),
  update:             (id, data)       => put(`/templates/${id}`, data),
  remove:             (id)             => del(`/templates/${id}`),
  duplicate:          (id, name)       => post(`/templates/${id}/duplicate`, { name }),
  categories:         ()               => get('/templates/categories'),
  preview:            (data)           => post('/templates/preview', data),
  // GET preview for listing page — loads saved template from DB and returns rendered HTML
  previewById:        (id, sampleData) => get(`/templates/${id}/preview`, sampleData ? { sampleData: JSON.stringify(sampleData) } : undefined),
  validate:           (template)       => post('/templates/validate', { template }),
  // Upload images (logo, banner, footer) via multipart/form-data after template is saved
  // Field names: logoImage, bannerImage, footerImage
  uploadImages:       (id, formData)   => axiosConfig.post(`${EMAIL_BASE}/templates/${id}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  /**
   * Unified file upload — single endpoint for images AND documents.
   * POST /email/templates/upload  (multipart, field: "file", max 10 MB)
   * Returns { url, s3Key, fileName, name, size, type, isImage }
   */
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axiosConfig.post(`${EMAIL_BASE}/templates/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  /**
   * Upload & attach a file to an existing template in one call.
   * POST /email/templates/:templateId/attachments  (multipart, field: "file")
   * Returns { attachment: { name, url, s3Key, type, size, isImage }, bodyAttachments: [...] }
   */
  addAttachment: (templateId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axiosConfig.post(`${EMAIL_BASE}/templates/${templateId}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  /**
   * Remove an attachment from a saved template.
   * DELETE /email/templates/:templateId/attachments  body: { s3Key } or { name }
   * Returns { bodyAttachments: [...] } — the updated list after removal.
   */
  removeAttachment: (templateId, identifier) => {
    return axiosConfig.delete(`${EMAIL_BASE}/templates/${templateId}/attachments`, { data: identifier });
  },
  // NOTE: notificationTypes and dataQueryHandlers are no longer used by the UI
  // Kept for backward compatibility — safe to remove if backend doesn't implement them
  notificationTypes:  ()               => get('/notification-types'),
  dataQueryHandlers:  ()               => get('/data-query-handlers'),
};

/* ──────────────── Campaign APIs ──────────────── */
export const campaignApi = {
  list:       (params)         => get('/campaigns/', params),
  create:     (data)           => post('/campaigns/', data),
  getById:    (id)             => get(`/campaigns/${id}`),
  update:     (id, data)       => put(`/campaigns/${id}`, { campaignId: id, ...data }),
  remove:     (id)             => del(`/campaigns/${id}`, { campaignId: id }),
  send:         (id)             => post(`/campaigns/${id}/send`, { campaignId: id }),
  costEstimate:  (id)             => get(`/campaigns/${id}/cost-estimate`),
  pause:      (id)             => post(`/campaigns/${id}/pause`, { campaignId: id }),
  resume:     (id)             => post(`/campaigns/${id}/resume`, { campaignId: id }),
  duplicate:  (id, name)       => post(`/campaigns/${id}/duplicate`, { campaignId: id, name }),
  test:       (id, testEmail)  => post(`/campaigns/${id}/test`, { campaignId: id, testEmail }),
  analytics:  (id)             => get(`/campaigns/${id}/analytics`),
  recipients: (id, params)     => get(`/campaigns/${id}/recipients`, params),
  sesStatus:  ()               => get('/campaigns/ses-status'),
  validateEmails: (id)         => post(`/campaigns/${id}/validate-emails`, { campaignId: id }),
};

/* ──────────────── Dashboard APIs ──────────────── */
export const dashboardApi = {
  overview:            (params) => get('/dashboard/overview', params),
  sendingStats:        (params) => get('/dashboard/sending-stats', params),
  topCampaigns:        (params) => get('/dashboard/top-campaigns', params),
  bounceStats:         (params) => get('/dashboard/bounce-stats', params),
  engagement:          ()       => get('/dashboard/engagement'),
  templatePerformance: (params) => get('/dashboard/template-performance', params),
};

/* ──────────────── Address Book (shared) ──────────────── */
export const addressBookApi = {
  list: () => axiosConfig.get('/addressbook/details'),
  getUsers: (data) => axiosConfig.post('/addressbook/users', data),
  getLabels: (data) => axiosConfig.post('/addressbook/labels', data),
};

/* ──────────────── Preferences APIs ──────────────── */
export const preferencesApi = {
  categories:    ()                       => get('/preferences/categories'),
  stats:         ()                       => get('/preferences/stats'),
  list:          (params)                 => get('/preferences/', params),
  getByEmail:    (email)                  => get(`/preferences/${encodeURIComponent(email)}`),
  updateByEmail: (email, preferences)     => put(`/preferences/${encodeURIComponent(email)}`, { preferences }),
  resubscribe:   (email, category)        => del(`/preferences/${encodeURIComponent(email)}/${category}`),
  history:       (email)                  => get(`/preferences/${encodeURIComponent(email)}/history`),
};

/* ──────────────── Health ──────────────── */
export const healthApi = {
  check: () => axiosConfig.get(`${EMAIL_BASE}/health`),
};
